import { useCallback, useMemo, useRef, useState } from 'react';
import { useSession, type OrgNode } from '@portal/platform/session';
import { useShellTexts } from '../i18n/use-shell-texts';
import { ancestorNames, useCurrentOrgNode } from './current-org-node';
import { buildFlatTree, initialExpandedIds } from './org-tree';
import { useDismiss } from './use-dismiss';

/** Solid legacy tag colours per node type. */
const TYPE_BADGE: Record<OrgNode['type'], string> = {
  ENTERPRISE: 'bg-[#625e95]',
  DEALER: 'bg-info',
  GROUP: 'bg-brand',
  DEPARTMENT: 'bg-danger',
};

/**
 * Pick the org node the whole portal is scoped to. Drawn as the menu's first entry, because it
 * changes every number on every page: a card captioned "Viser data for" with the node's name and
 * where it sits, or, in the folded menu, a tile with the name's initials. Shows node names only —
 * never an id (CLAUDE.md #4): a node missing from the tree reads as "Organisation".
 */
export function OrgNodeSelector({ variant = 'card' }: { variant?: 'card' | 'rail' }) {
  const t = useShellTexts().orgNodeSelector;
  const { orgTree, scope, platform } = useSession();
  const nodes = useMemo(() => orgTree?.nodes ?? [], [orgTree]);
  const currentId = scope.current?.id ?? null;
  const current = useCurrentOrgNode();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState<Set<string> | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(ref, open, close);

  // Seeded each time the menu opens, so it starts at the current node.
  const expandedIds = expanded ?? initialExpandedIds(nodes, currentId);
  const rows = buildFlatTree(nodes, expandedIds);
  const currentName = current?.name ?? scope.current?.name ?? t.organization;
  const path = current ? ancestorNames(current, nodes) : [];

  const toggleExpanded = (id: string) => {
    const next = new Set(expandedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setExpanded(next);
  };

  const trigger = {
    type: 'button' as const,
    title: t.selectOrganization,
    'aria-haspopup': 'true' as const,
    'aria-expanded': open,
    'aria-label': `${t.showingDataFor}: ${currentName}`,
    onClick: () => {
      setExpanded(null);
      setOpen(value => !value);
    },
  };

  return (
    <div ref={ref} className="relative">
      {variant === 'rail' ? (
        <button
          {...trigger}
          className="flex h-10 w-10 items-center justify-center rounded-bs border border-brand/60 bg-brand/5 text-xs font-bold text-brand-dark uppercase hover:bg-brand/10 focus-visible:outline-2 focus-visible:outline-input-focus"
        >
          {initials(currentName)}
        </button>
      ) : (
        <button
          {...trigger}
          className="flex w-full items-center gap-2 rounded-bs-lg border border-brand/60 bg-brand/5 px-3 py-2 text-left hover:bg-brand/10 focus-visible:outline-2 focus-visible:outline-input-focus"
        >
          <span className="min-w-0 flex-1">
            <span className="block text-[11px] font-bold tracking-wide text-brand-dark uppercase">
              {t.showingDataFor}
            </span>
            <span className="block truncate font-bold text-ink">{currentName}</span>
            {path.length > 0 ? (
              <span className="block truncate text-xs text-ink-muted">{path.join(' › ')}</span>
            ) : null}
          </span>
          <svg
            className="h-4 w-4 shrink-0 text-ink-muted"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
            />
          </svg>
        </button>
      )}
      {open ? (
        <div
          className={`absolute z-[1000] max-h-[400px] min-w-[300px] overflow-y-auto rounded-bs border border-black/15 bg-white p-2 shadow-lg ${variant === 'rail' ? 'top-0 left-full ml-2' : 'top-full left-0 mt-1'}`}
        >
          {orgTree === undefined ? (
            <p role="status" className="p-2 text-xs text-ink-muted">
              {t.loading}
            </p>
          ) : (
            <ul aria-label={t.selectOrganization}>
              {rows.map(row => {
                const selected = row.node.id === currentId;
                return (
                  <li
                    key={row.node.id}
                    className="flex items-center gap-1 py-0.5"
                    style={{ paddingLeft: `${row.level * 1.5}rem` }}
                  >
                    {row.hasChildren ? (
                      <button
                        type="button"
                        className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-ink-muted hover:text-ink"
                        title={row.isExpanded ? t.collapse : t.expand}
                        aria-label={`${row.isExpanded ? t.collapse : t.expand} ${row.node.name}`}
                        aria-expanded={row.isExpanded}
                        onClick={() => toggleExpanded(row.node.id)}
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d={
                              row.isExpanded
                                ? 'm19.5 8.25-7.5 7.5-7.5-7.5'
                                : 'm8.25 4.5 7.5 7.5-7.5 7.5'
                            }
                          />
                        </svg>
                      </button>
                    ) : (
                      <span className="inline-block w-5" />
                    )}
                    <button
                      type="button"
                      aria-current={selected ? 'true' : undefined}
                      className={`flex flex-1 items-center gap-2 rounded-bs border px-2 py-1.5 text-left text-sm hover:bg-cap ${selected ? 'border-brand bg-alert-success-bg font-bold' : 'border-transparent'}`}
                      onClick={() => {
                        platform.scope.resetTo(row.node);
                        setOpen(false);
                      }}
                    >
                      <span
                        className={`inline-block rounded-bs px-1.5 py-0.5 text-[10px] font-bold text-white uppercase ${TYPE_BADGE[row.node.type]}`}
                      >
                        {t.types[row.node.type]}
                      </span>
                      <span className="flex-1 text-ink">{row.node.name}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** "Nellemann Glostrup" → "NG": the folded menu's stand-in for the name. */
function initials(name: string): string {
  const words = name.split(/\s+/).filter(Boolean);
  return (words.length > 1 ? `${words[0]![0]}${words[1]![0]}` : name.slice(0, 2)) || '?';
}
